import { Play } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { youtubeEmbedUrl, youtubeThumbnailUrl } from "@/lib/youtube"

/**
 * OKS-761 — Inline YouTube player for a creator Learn LINK module whose
 * `link_url` parses to a YouTube video id (classification done upstream in
 * `CreatorModuleContent`; this component is only mounted with a valid id).
 *
 * Lite-embed (TRD KD-1, UX Spec §2): paint the `hqdefault` thumbnail + a
 * centered play overlay first; swap to a `youtube-nocookie` iframe (with
 * user-initiated `autoplay=1`) on the first click. This keeps a third-party
 * iframe + cookies OFF the page until the creator chooses to watch (privacy)
 * and keeps the module page light (perf) — while still playing IN-PAGE, never
 * a new tab.
 *
 * OKS-721 — this REPLACES the external-link card for YouTube modules; it is the
 * single affordance. There is deliberately no "watch on YouTube" secondary CTA.
 *
 * The 16:9 wrapper reuses the uploaded-`<video>` shape's container styling
 * (`aspect-video w-full rounded-md bg-black`) so the two video surfaces look
 * identical and the frame reserves its space with no layout shift (UX Spec §3
 * Loading) and no horizontal overflow at 375px (§5).
 */
export interface CreatorModuleYoutubePlayerProps {
  /** An 11-char id already validated by `parseYouTubeId`. */
  videoId: string
  /** Module title — used for the iframe's accessible `title`. */
  title: string
}

export function CreatorModuleYoutubePlayer({
  videoId,
  title,
}: CreatorModuleYoutubePlayerProps) {
  const { t } = useTranslation()
  const [activated, setActivated] = useState(false)

  return (
    <div
      className="aspect-video w-full overflow-hidden rounded-md bg-black"
      data-testid="learn-module-youtube"
    >
      {activated ? (
        <iframe
          src={`${youtubeEmbedUrl(videoId)}?autoplay=1`}
          title={t("learn.module.video_title", { title })}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
          allowFullScreen
          className="size-full border-0"
          data-testid="learn-module-youtube-frame"
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setActivated(true)
          }}
          className="group focus-visible:ring-ring relative flex size-full items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inset"
          aria-label={t("learn.module.play_video")}
          data-testid="learn-module-youtube-play"
        >
          <img
            src={youtubeThumbnailUrl(videoId)}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 size-full object-cover"
            data-testid="learn-module-youtube-thumbnail"
          />
          <span className="relative flex size-16 items-center justify-center rounded-full bg-black/60 transition-transform group-hover:scale-110">
            <Play className="size-8 translate-x-0.5 fill-white text-white" />
          </span>
        </button>
      )}
    </div>
  )
}
